import {
    Unsubscribe,
    get,
    onChildAdded,
    onChildChanged,
    onChildRemoved,
} from '@/features/firebase/adapter/database'
import FirebaseDatabaseRefService from '@/features/firebase/services/database-ref'
import { Order } from '@/features/orders/types/app'
import { transformOrder } from '@/features/orders/utils/transforms'
import { useAppDispatch } from '@/hooks/redux'
import { addOrder, clearOrders, removeOrder, setOrders, updateOrder } from '@/redux/reducers/orders'
import { useEffect } from 'react'
import invariant from 'tiny-invariant'

export const useFirebaseOrdersListeners = () => {
    const dispatch = useAppDispatch()

    useEffect(() => {
        let disposed = false
        let unsubscribeRemoved: Unsubscribe | null = null
        let unsubscribeChanged: Unsubscribe | null = null
        let unsubscribeAdded: Unsubscribe | null = null

        const listen = async () => {
            const ordersRef = FirebaseDatabaseRefService.orders()

            unsubscribeRemoved = onChildRemoved(ordersRef, (snapshot) => {
                if (disposed) return

                invariant(snapshot.key, "Snapshot key doesn't exist")

                dispatch(removeOrder(snapshot.key))
            })

            unsubscribeChanged = onChildChanged(ordersRef, (snapshot) => {
                if (disposed) return

                invariant(snapshot.key, "Snapshot key doesn't exist")

                dispatch(updateOrder({ uuid: snapshot.key, order: transformOrder(snapshot.val()) }))
            })

            const snapshot = await get(ordersRef)

            if (disposed) return

            const orders: Record<string, Order> = {}

            snapshot.forEach((child) => {
                orders[child.key] = transformOrder(child.val())
            })

            dispatch(setOrders(orders))

            unsubscribeAdded = onChildAdded(ordersRef, (snapshot) => {
                if (disposed) return

                invariant(snapshot.key, "Snapshot key doesn't exist")

                dispatch(addOrder({ uuid: snapshot.key, order: transformOrder(snapshot.val()) }))
            })
        }

        listen()

        return () => {
            disposed = true

            if (unsubscribeRemoved) unsubscribeRemoved()

            if (unsubscribeChanged) unsubscribeChanged()

            if (unsubscribeAdded) unsubscribeAdded()

            dispatch(clearOrders())
        }
    }, [dispatch])
}
